// src/main/shell/symbolGrid/pagedBackend.ts
//
// The backend SEAM for IndexerPagedView. The paged-tree grid engine is generic
// over WHERE its rows + values + mutations come from; this interface is that
// boundary, so ONE engine serves both a PLC controller's symbol catalog and an
// integration's schema. Two implementations:
//
//   • controllerPagedBackend — a controller's symbols (SymbolIndex + the plugin
//     RPCs). Forwards 1:1 to today's backend.api calls, so the controller grid
//     is byte-identical to before this seam was extracted.
//   • integrationPagedBackend — an integration's schema (IntegrationIndex). The
//     live value plane IS wired: window-follow values + sparkline charts stream
//     for the viewport's leaves (the api-gateway drives a viewport-scoped
//     observe-lease). VALUE writes stay inert — integration values are written by
//     models (NATS), not the UI — and so do priority + lock (a value-kind
//     source is read-only, observe-while-watched). Only user LABELS apply, as a display
//     overlay over cryptic source paths.
import * as Comlink from 'comlink';
import { takeFirstPage, takeIntegrationFirstPage } from './pagePrefetch';
import type { BackendClient } from '../../bridge/backend-client';
import { getSession } from '@vitesse/base-ui';
import { Plugins } from '@vitesse/proto/vitesse/v1/command_pb.js';
import { mayReadValues } from '../../components/integration/integration-access';
import type {
  PageQuery,
  SymbolPageData,
  SymbolOpResult,
  BulkProgress,
  BulkOutcome,
  BulkOp,
  ApplyToAllOutcome,
} from '../../../core/bridge/connect-client';

/** Per-symbol delete outcome (the single-item unwrap of the batch RPC). */
export interface DeleteOutcome {
  ok: boolean;
  error: string;
}

/**
 * The backend operations IndexerPagedView needs, abstracted from controller-vs-
 * integration scope. The adapter holds gatewayId / scopeId / pluginId internally;
 * the engine just calls these scope-free methods.
 */
/** The per-action answers the grid needs. Plain booleans, recomputed when the
 *  session changes, so the render path costs a property read rather than a policy
 *  evaluation per row per frame. */
export interface GridPermissions {
  /** Edit a value — Plugins.WriteSymbols (org:symbol:write). */
  readonly write: boolean;
  /** Add or delete a symbol — Plugins.DeleteSymbols (org:symbol:config). */
  readonly config: boolean;
  /** The padlock and apply-to-all — Plugins.SetSymbolLock (org:symbol:lock). */
  readonly lock: boolean;
  /** The priority "!" — Plugins.SetPollingPriority, which is gated on the
   *  telemetry CAPABILITY rather than a permission: it is bought, not granted. */
  readonly priority: boolean;
}

export interface PagedBackend {
  /** Controller id or integration id — the grid's storage namespace + getControllerId(). */
  readonly scopeId: string;
  /** Gateway-scoped unique key for the nav-state cache (so two gateways' same-id
   *  scopes never collide on scroll/tree/filter state). */
  readonly storageKey: string;
  /** When true the live value lease FOLLOWS THE VISIBLE VIEWPORT (+ a small scroll
   *  buffer) instead of the whole fetched window — so the backend produces only
   *  what's on screen ("nothing more nothing less"). Integrations set this: each
   *  leased path is a real external subscribe/poll/query, so over-leasing the
   *  ~1200-row window costs real backend ops. Controllers leave it false — a PLC
   *  symbol is already polled regardless, and the window-lease (re-scoped on the
   *  prefetch cadence, no per-scroll churn) is the established, working path. */
  readonly valueScopeViewport?: boolean;
  /** When true the grid hides per-row WRITE affordances (priority + lock): the
   *  source is read-only observe-while-watched (integrations), so those PLC-PDU
   *  concepts don't apply. Labels (a display overlay) still apply. */
  readonly readOnlyActions?: boolean;

  /**
   * Which write affordances the USER is permitted, read from the annotations on
   * the RPCs behind them.
   *
   * Distinct from `readOnlyActions`, and the two must not be merged. That says the
   * SOURCE has no such concept (an integration has no PLC polling or write-lock);
   * this says the PERSON may not do it. Same button hidden, different fact, and
   * only one of them is worth telling the user they could buy or be granted.
   *
   * Four separate flags because the server guards these with four different
   * permissions — a user can hold org:symbol:write and not org:symbol:config, so
   * one "may write" boolean would either hide a control they have or show one they
   * do not. Absent ⇒ permitted, matching the fail-open posture everywhere else.
   */
  readonly may?: GridPermissions;

  /** Whether the owning plugin enforces the symbol write-lock. Held on the source
   *  so a permission refresh can recompute `may` without re-reading a descriptor
   *  it has no handle on. Absent ⇒ false: see gridPermissions. */
  readonly pluginCanLock?: boolean;

  /** One viewport page (request/reply tree fetch). */
  fetchPage(query: PageQuery): Promise<SymbolPageData>;
  /** Hover-prewarmed first page for a zero-RTT first paint, or null if the source
   *  has no prefetch cache. */
  takeFirstPage(): Promise<SymbolPageData | null>;
  /** A tagged background twin of `query` whose ONLY purpose is to re-scope the
   *  window-follow value stream server-side (used after a prefetch HIT, whose page
   *  was fetched untagged). No-op for sources without a live value stream. */
  scopeValueStream(query: PageQuery): void;
  /** Open the window-follow value stream, or null if the source has no live value
   *  plane yet (integration values are the 5b layer). */
  openWindowStream(clientSession: string, windowMs: number): Promise<MessagePort | null>;
  /** Open the STRUCTURE stream for `query` — the dumb-viewport page source. The
   *  backend pushes the snapshot page + every recomputed page on catalog change
   *  (server-side hash-compared); the view renders each and re-opens (closing the
   *  port) on nav. Same SymbolQuery→stream contract for controllers + integrations. */
  openStructureStream(query: PageQuery): Promise<MessagePort | null>;
  /** Full path enumeration for select-all bulk (empty if unsupported). */
  fetchAllPaths(filter: string): Promise<string[]>;

  // ── mutations (read-only sources no-op / reject) ──
  putLabel(symbolId: string, label: string): Promise<void>;
  setPriority(symbolId: string, on: boolean): Promise<void>;
  /** Lock/unlock ONE symbol. Resolves with the plugin's per-symbol result so the
   *  caller can tell a refusal from a success — the RPC succeeds as a whole even
   *  when the plugin declines the symbol (a lock on a read-only or non-scalar
   *  symbol protects nothing). `undefined` when the source has no lock concept. */
  setLocked(symbolId: string, locked: boolean): Promise<SymbolOpResult | undefined>;
  deleteSymbol(symbolId: string): Promise<DeleteOutcome>;
  writeValue(symbolId: string, value: boolean | string, dataType: string): Promise<SymbolOpResult>;
  bulkSetLocked(paths: string[], locked: boolean, onProgress: (p: BulkProgress) => void): Promise<BulkOutcome>;
  bulkSetPriority(paths: string[], priority: boolean, onProgress: (p: BulkProgress) => void): Promise<BulkOutcome>;
  applyToAll(op: BulkOp): Promise<ApplyToAllOutcome>;
}

/**
 * Controller adapter — forwards every op 1:1 to the controller-scoped backend.api
 * calls the grid used before the seam existed. The controller path is therefore
 * behavior-identical; this is a pure refactor for it.
 */
/**
 * The user's write permissions for a PLC grid, from the annotations on the RPCs
 * behind each affordance — never a restated permission string.
 *
 * Recomputed rather than read per row: the grid updates 80 pooled rows per pass,
 * and `allowsRpc` walks protobuf method options, so evaluating inside the render
 * loop would put reflection on the hot path. SymbolGrid re-reads this on session
 * change, the same way it re-renders on a locale change.
 */
export function gridPermissions(pluginCanLock = true): GridPermissions {
  const s = getSession();
  if (!s) return { write: true, config: true, lock: true, priority: true }; // fail-open
  return {
    write: s.allowsRpc(Plugins.method.writeSymbols),
    config: s.allowsRpc(Plugins.method.deleteSymbols),
    // BOTH, because the padlock and the bulk bar reach each flag two ways: the
    // per-id verb, and the whole-controller fast path. The comment here used to
    // say they "carry the same permission today… but deriving it from only one
    // would leave the grid silently wrong the day they diverge." They have now
    // diverged, and for a reason: the single generic ApplyToAll was split, because
    // gating bulk-lock and bulk-priority identically let bulk-priority bypass the
    // telemetry PLAN that refuses the same change one symbol at a time.
    //
    // So each control now asks about BOTH verbs on its own axis — locking is a
    // permission, prioritizing is priced — and a control is offered only when the
    // caller may take both routes to it.
    /*
     * AND the PLUGIN must support locking, not just the caller be allowed to ask.
     *
     * The lock is enforced by the plugin, at the write path — that is what makes
     * it the right protection for an estop, since a locked symbol is refused below
     * every role and every cloud-side caller. A plugin that has not implemented it
     * cannot refuse anything, so a padlock shown against that plugin is worse than
     * a control that errors: it LOOKS like protection and is not.
     *
     * Defaults true so a caller that cannot see a descriptor is unchanged; the
     * grid passes the real flag.
     */
    lock:
      pluginCanLock &&
      s.allowsRpc(Plugins.method.setSymbolLock) &&
      s.allowsRpc(Plugins.method.applyLockToAll),
    priority: s.allowsRpc(Plugins.method.setPollingPriority) && s.allowsRpc(Plugins.method.applyPriorityToAll),
  };
}

export function controllerPagedBackend(
  gatewayId: string,
  controllerId: string,
  backend: BackendClient,
  pluginId: string,
  /** Whether the owning plugin ENFORCES the write-lock — from its descriptor. */
  pluginCanLock = false,
): PagedBackend {
  return {
    scopeId: controllerId,
    storageKey: `${gatewayId}:${controllerId}`,
    pluginCanLock,
    may: gridPermissions(pluginCanLock),

    fetchPage: (q) => backend.api.fetchSymbolPage(gatewayId, controllerId, q),
    takeFirstPage: async () => (await takeFirstPage(gatewayId, controllerId)) ?? null,
    scopeValueStream: (q) => {
      // A prefetched page was fetched UNTAGGED, so the follow window hasn't seen
      // it — fire a tagged twin (same query, identical rows) purely to scope the
      // value stream server-side. One cheap indexer hit; result discarded.
      void backend.api.fetchSymbolPage(gatewayId, controllerId, q).catch(() => {});
    },
    openWindowStream: (clientSession, windowMs) =>
      backend.api.subscribeWindowValues(gatewayId, controllerId, clientSession, windowMs),
    openStructureStream: (q) => backend.api.subscribeWindowSymbolPage(gatewayId, controllerId, q, false),
    fetchAllPaths: (filter) => backend.api.fetchAllSymbolPaths(gatewayId, controllerId, filter),

    putLabel: (symbolId, label) => backend.api.putLabel(gatewayId, controllerId, symbolId, label),
    setPriority: (symbolId, on) => backend.api.setPollingPriority(gatewayId, pluginId, controllerId, [symbolId], on),
    setLocked: async (symbolId, locked) =>
      (await backend.api.setSymbolsLocked(gatewayId, pluginId, controllerId, [symbolId], locked))[0],
    deleteSymbol: async (symbolId) => {
      const res = await backend.api.deleteSymbols(gatewayId, pluginId, controllerId, [symbolId]);
      const item = res.results[0];
      return { ok: res.ok && (item?.ok ?? false), error: item?.error || res.error };
    },
    writeValue: async (symbolId, value, dataType) => {
      const results = await backend.api.writeSymbols(gatewayId, pluginId, controllerId, [{ path: symbolId, value, dataType }]);
      return results[0] ?? { symbolId, ok: false, error: 'no result returned', errorCode: 'NO_RESULT' };
    },
    bulkSetLocked: (paths, locked, onProgress) =>
      backend.api.bulkSetLocked(gatewayId, pluginId, controllerId, paths, locked, Comlink.proxy(onProgress)),
    bulkSetPriority: (paths, priority, onProgress) =>
      backend.api.bulkSetPriority(gatewayId, pluginId, controllerId, paths, priority, Comlink.proxy(onProgress)),
    applyToAll: (op) => backend.api.applyToAll(gatewayId, pluginId, controllerId, op),
  };
}

const READONLY_BULK = (paths: string[]): BulkOutcome => ({ total: paths.length, ok: 0, failed: 0, failures: [] });

/**
 * Integration adapter — the schema tree comes from IntegrationIndex (same wire
 * contract as the symbol catalog). Live values flow via the window-follow value
 * plane (same machinery as a controller grid): the api-gateway re-scopes the
 * stream from the view's tagged page fetches AND drives the edge observe-lease so
 * the window's leaves get produced (an integration value exists only while
 * observed). Mutations remain inert:
 *   • the browser never mutates integration state — writes are model→service
 *     (NATS) by design, and a value-kind source is read-only — so the mutation
 *     methods are safe no-ops/rejects (the grid also gates them on the rows'
 *     read-only permission, so they're not reached in practice).
 */
export function integrationPagedBackend(
  gatewayId: string,
  integrationId: string,
  backend: BackendClient,
): PagedBackend {
  return {
    scopeId: integrationId,
    storageKey: `${gatewayId}:integration:${integrationId}`,
    valueScopeViewport: true, // lease only the visible viewport — each leased path is a real external subscribe/poll
    readOnlyActions: true, // observe-while-watched, read-only: no priority/lock per row (labels still apply)

    fetchPage: (q) => backend.api.fetchIntegrationPage(gatewayId, integrationId, q),
    takeFirstPage: async () => (await takeIntegrationFirstPage(gatewayId, integrationId)) ?? null,
    scopeValueStream: (q) => {
      if (!mayReadValues(getSession())) return;
      // Tagged twin (same query, identical rows) so the follow window scopes the
      // value stream server-side AND the api-gateway extends the observe-lease to
      // the page's leaves. One cheap IntegrationIndex hit; result discarded.
      void backend.api.fetchIntegrationPage(gatewayId, integrationId, q).catch(() => {});
    },
    openWindowStream: (clientSession, windowMs) =>
      mayReadValues(getSession())
        ? backend.api.subscribeIntegrationWindowValues(gatewayId, integrationId, clientSession, windowMs)
        : Promise.resolve(null),
    openStructureStream: (q) => backend.api.subscribeWindowSymbolPage(gatewayId, integrationId, q, true),
    fetchAllPaths: async () => [], // no select-all bulk on a read-only schema

    putLabel: async () => {}, // integrations have no user-label path (yet)
    setPriority: async () => {}, // no polling-priority concept
    setLocked: async () => undefined, // no lock concept
    deleteSymbol: async () => ({ ok: false, error: 'delete is not supported for integrations' }),
    // Publish one whole message to a broker topic (or the whole body of any other
    // writable integration path). This used to refuse outright — "integration
    // values are written by models, not the UI" — which was true of the plumbing
    // rather than of the domain: Integrations.Write has always existed, the
    // endpoint and table screens already call it, and a topic row has carried
    // Writable: true since the whole-topic leaf was added.
    //
    // WHOLE-VALUE ONLY, and that is the safety property, not a limitation. A broker
    // publishes whole messages — you cannot partial-publish one field of an MQTT
    // payload — so the editor hands back the entire document the user just looked
    // at and edited. Nothing can be silently dropped, because nothing was hidden.
    // Editing a single FLATTENED FIELD is a different feature (it has to merge into
    // the last-seen payload) and deliberately does not ride this path.
    //
    // String-valued because that is the wire contract: integrationWrite binds every
    // value as stringVal, and the topic drivers take `values["payload"]` verbatim
    // as the message bytes (driver.PayloadFromValues).
    writeValue: async (symbolId, value) => {
      try {
        await backend.api.integrationWrite(gatewayId, integrationId, symbolId, { payload: String(value) });
        return { symbolId, ok: true, error: '', errorCode: '' };
      } catch (err) {
        // The service's refusal is already prose (the universal Result envelope) —
        // which target, and why — so surface it rather than rewording it. The editor
        // keeps the dialog open and shows this inline.
        return {
          symbolId,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
          errorCode: 'WRITE_FAILED',
        };
      }
    },
    bulkSetLocked: async (paths) => READONLY_BULK(paths),
    bulkSetPriority: async (paths) => READONLY_BULK(paths),
    applyToAll: async () => ({ ok: true, affected: 0, error: '' }),
  };
}
